import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State } from '@stencil/core';
import _ from 'lodash';
import { QueryStore, ZoneStore, TemplatingInterfaces, ComponentActionEvent, MoTypeaheadTemplateQueryMeta, TypeaheadUtils, ResultUtils, QueryStoreConfig, QueryRequestParams } from 'ui-common';
import { embeddableAgent } from '../../services/embeddable-agent';
import { convertDeepLinkingPropToObject, getConfig } from '../../services/embeddable-helper';
import { LwSearchaheadConfig } from './interfaces';

@Component({
  tag: 'lw-searchahead',
  styleUrl: 'lw-searchahead.scss',
})
export class LwSearchahead {
  @Prop() config: LwSearchaheadConfig | string;
  @Prop() group: string;
  @Prop() deepLinkingStates = { persistQuery: 'query', persistFilters: 'searchaheadFilters' };

  @Prop() placeholder: string;
  @Prop() typeDebounce: number;

  @State() showResults = false;
  @State() activated = false;
  @State() maxLengthExceeded = false;
  @State() docs = [];
  @State() imageError;

  @Event() query: EventEmitter<MoTypeaheadTemplateQueryMeta>;

  @Element() element: HTMLElement;

  searchaheadStore: QueryStore;
  configKeys: string[] = ['placeholder', 'typeDebounce'];
  queryParamConfig: string[] = ['limit', 'filters', 'highlight', 'sort', 'fieldList'];
  searchQuery: string;
  disconnected: boolean = false;
  persistQuery: string;
  persistFilters: string;

  mergedConfig: LwSearchaheadConfig = {
    placeholder: 'Search',
    typeDebounce: 200,
    minTriggerChars: 3,
    highlight: true,
    limit: 20,
  };

  @Listen('componentAction')
  onComponentAction(event: CustomEvent<ComponentActionEvent>) {
    if (event.detail?.action === TemplatingInterfaces.ComponentAction.TYPEAHEAD_DOC_SELECTED) {
      this.onClickTypeaheadDoc(event.detail.payload);
      this.showResults = false;
    }
  }

  @Listen('focusin')
  onFocusIn() {
    // Load all the popper/results dependent modules on-demand.
    this.activated = true;
  }

  @Listen('mouseup', { target: 'document' })
  async doOnMouseUp(event) {
    if (!event.target.closest('mo-search-bar') || event.target.matches('mo-search-bar .search-bar-clear')) {
      this.showResults = false;
    }
  }

  onClickTypeaheadDoc = (doc) => {
    this.searchQuery = this.cleanQuery(doc?.fields[ResultUtils.getTitleField(doc?.fields)]);
    _.invoke(this.element.querySelector('mo-search-bar'), 'setQuery', this.searchQuery);
    if (this.validate(this.searchQuery)) {
      this.onQueryUpdated({ detail: { query: this.searchQuery, trigger: 'click', docId: doc.id, doc } })
      this.showResults = false;
    }
  };

  getMoSearchBar = () => this.element.querySelector('mo-search-bar');
  cleanQuery = (query: string) => TypeaheadUtils.removeEmphasis(query);
  clearQuery = (focus: boolean = false) => {
    this.searchQuery = '';
    this.persistQuery = '';
    this.maxLengthExceeded = false;
    const moSearchBar = this.getMoSearchBar() as HTMLElement;

    return _.invoke(moSearchBar, 'clearQuery', focus);
  }

  hasExceededMaxLength = (query: string) => _.size(query) > this.mergedConfig.maxLength;
  validate = (query: string) => {
    this.maxLengthExceeded = this.hasExceededMaxLength(query);
    return !this.maxLengthExceeded;
  }

  onKeyUp = (event) => {
    const { key, target } = event;
    const query = target.value.trim();
    const minTriggerChars = this.mergedConfig.minTriggerChars;

    if (this.validate(query)) {
      const isTyping = query !== '';

      this.showResults = _.size(query) >= minTriggerChars;
      if (key === 'Enter') {
        this.updateQuery(query, 'enter');
      } else if (isTyping && query !== this.searchQuery && this.searchaheadStore) {
        if (this.showResults) {
          this.searchaheadStore.setQuery(query);
        }
      }
      this.searchQuery = query;
    }
  };

  onQueryAction = (query: string, trigger?: string) => {
    if (_.isEmpty(query)) {
      this.maxLengthExceeded = false;
    }
    if (trigger && this.validate(query)) {
      this.updateQuery(query, trigger);
    }
  }

  updateQuery = (query: string, trigger: string) => {
    this.onQueryUpdated({ detail: { query: query || '*:*', trigger } });
    this.showResults = false;
  }

  componentWillLoad() {
    const parsedConfig = getConfig(this.config) || {};

    _.merge(this.mergedConfig, parsedConfig, _(this).pick(this.configKeys).omitBy(_.isEmpty).value());

    embeddableAgent.configureDeepLinking(this);
    this.setupStore();
  }

  disconnectedCallback() {
    this.disconnected = true;
  }

  setupStore = () => {
    const searchStoreConfig: QueryStoreConfig = { type: 'search' };
    const queryParams: QueryRequestParams = _.pick(this.mergedConfig, this.queryParamConfig);

    if (!_.isEmpty(this.persistQuery)) {
      _.set(searchStoreConfig, 'queryParams.query', this.persistQuery);
    }

    queryParams.filters = convertDeepLinkingPropToObject(this, 'persistFilters');

    this.searchaheadStore = embeddableAgent.getStore(this, {
      type: 'searchahead',
      requestDebounceWait: this.mergedConfig.typeDebounce,
      queryParams,
    });

    this.searchaheadStore.subscribe((zoneStores: ZoneStore[]) => {
      this.docs = _.get(_.first(zoneStores), 'data.docs', []);
    }, this);

    const searchStore = embeddableAgent.getStore(this, searchStoreConfig);

    searchStore.subscribe((_zoneStores: ZoneStore[]) => {
      if (!this.disconnected && _.includes(['*:*', ''], searchStore.meta.currentParams.query)) {
        this.clearQuery();
      }
    });
  };

  onQueryUpdated = ({ detail }) => {
    embeddableAgent.setQuery(detail.query, this);
    this.query.emit(detail);
    this.persistQuery = encodeURIComponent(detail.query);
  };

  getPopperContent = () => {
    const config = getConfig(this.config);
    return <div class="result-list">
      {_.map(this.docs, doc => {
        return (
          <div class="result-list-item" onClick={() => this.onClickTypeaheadDoc(doc)}>
            <mo-doc-image doc={doc} imageField={this.getImageField(config)} />
            <div class="item" innerHTML={doc.fields[ResultUtils.getTitleField(doc.fields, config)]} />
          </div>
        )
      })}
    </div>
  }

  getImageField = (config) => _.get(config, 'displayFields.image') || 'document.thumbnailImage';

  render() {
    return (
      <Host class="lw-embeds">
        <mo-search-bar
          class={{ 'max-length-exceeded': this.maxLengthExceeded, 'show-results': this.showResults }}
          onKeyUp={this.onKeyUp}
          placeholder={this.mergedConfig.placeholder}
          searchWhileTyping={true}
          existingQuery={this.persistQuery}
          defaultQuery=""
          action={this.onQueryAction}>
          {this.activated && this.searchaheadStore && !_.isEmpty(this.docs) && (
            <mo-popper
              class="typeahead-wrapper"
              trigger="always"
              placement="bottom-start"
              autoWidth={true}
              getContent={() => (this.showResults && this.getPopperContent())}
            />
          )}
        </mo-search-bar>

        {this.maxLengthExceeded && <div class="inline-error-text">Query must not exceed {this.mergedConfig.maxLength} characters.</div>}
      </Host>
    );
  }
}
